import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import React, { useContext, useEffect, useState } from 'react';
import { off, primary } from '../theme/colors';

import { ConsentContext } from '../context/ConsentContext';
import EditAddressModal from './Modal/EditAddressModal';
import Icon from './Icon';
import LoadingBarWrapper from './LoadingBarWrapper';
import ReconfirmCard from './ReconfirmCard';
import checkIconSvg from '../assets/svg/check-mark.svg';
import currentUserPropTypes from '../propTypes/currentUserPropTypes';
import { getTodayDateString } from '../utils/getTodayDateString';
import { getUserDetails } from '../api/TrustPower';
import { isWithinReconsentNoticePeriod } from '../utils/isWithinReconsentNoticePeriod';
import moment from 'moment';
import notAvailableIconSvg from '../assets/svg/not-available.svg';
import { parseAddressString } from '../utils/parseAddress';
import styled from 'styled-components';
import { useNavigate } from 'react-router';

const BorderedRow = styled(Row)`
  border-bottom: 1px solid #afb6b9;
  padding-bottom: 10px;
  padding-top: 10px;
`;

const FinalRow = styled(Row)`
  padding-bottom: 10px;
  padding-top: 10px;
`;

const StyledH4 = styled.h4`
  text-transform: uppercase;
  font-weight: bold;
  font-size: 16px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: 1px;
  color: #ff0085;
  margin-bottom: 0;
`;

const StyledRowTitle = styled.h5`
  color: ${off};
  font-weight: normal;
  margin-bottom: 0;

  @media (max-width: 768px) {
    margin-right: 10px;
  }
`;

const StyledCard = styled(Card)`
  border-color: #afb6b9;
`;

const SmartMeterDetails = ({ currentUser }) => {
  const navigate = useNavigate();
  const { reconfirmLastSeen } = useContext(ConsentContext);
  const [showEditAddressForm, setShowEditAddressForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [registered, setRegistered] = useState(null);
  const [gasLastUpdated, setGasLastUpdated] = useState(null);
  const [elecLastUpdated, setElecLastUpdated] = useState(null);
  const [gasDataRange, setGasDataRange] = useState(null);
  const [elecDataRange, setElecDataRange] = useState(null);
  const [elecMpxn, setElecMpxn] = useState(null);
  const [reconsentDue, setReconsentDue] = useState(null);
  const [showReconsentBanner, setShowReconsentBanner] = useState(false);

  useEffect(() => {
    getUserDetails()
      .then(res => {
        if (res?.consent?.reconsent_due) {
          const reconsentDueResult = res?.consent?.reconsent_due;

          setReconsentDue(reconsentDueResult);

          if (isWithinReconsentNoticePeriod(reconsentDueResult)) {
            setShowReconsentBanner(
              isWithinReconsentNoticePeriod(reconsentDueResult),
            );

            if (reconsentDueResult < Date.now() / 1000) {
              navigate('/reconfirm/now');
            } else if (reconfirmLastSeen !== getTodayDateString()) {
              navigate('/reconfirm/soon');
            }
          }
        }

        setRegistered(res?.consent?.registered || null);

        const gasStartDate =
          moment(res?.meters?.gas?.data_range?.start * 1000) || null;
        const gasEndDate =
          moment(res?.meters?.gas?.data_range?.end * 1000) || null;
        const gasDaysBetween = moment(gasEndDate).diff(gasStartDate, 'days');
        if (gasDaysBetween) setGasDataRange(gasDaysBetween);

        const elecStartDate =
          moment(res?.meters?.electricity?.data_range?.start * 1000) || null;
        const elecEndDate =
          moment(res?.meters?.electricity?.data_range?.end * 1000) || null;
        const elecDaysBetween = moment(elecEndDate).diff(elecStartDate, 'days');
        if (elecDaysBetween) setElecDataRange(elecDaysBetween);

        if (res?.meters?.electricity?.data_range?.end) {
          setElecLastUpdated(
            moment(res?.meters?.electricity?.data_range?.end * 1000).format(
              'DD/MM/YYYY, h:mma',
            ),
          );
        }
        if (res?.meters?.gas?.data_range?.end) {
          setGasLastUpdated(
            moment(res?.meters?.gas?.data_range?.end * 1000).format(
              'DD/MM/YYYY, h:mma',
            ),
          );
        }

        setElecMpxn(res?.meters?.electricity?.mpxn || null);

        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        setLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatRegisteredDate = date => {
    return moment(date * 1000).format('DD/MM/YYYY, HH:mm');
  };

  const loopLink = 'https://kb.loop.homes';
  const notAvailableLink =
    loopLink +
    '/ive-completed-the-sign-up-flow-successfully-but-im-not-seeing-any-data';
  const whatIsMpanLink = loopLink + '/what-is-mpan';

  return (
    <>
      <EditAddressModal
        show={showEditAddressForm}
        onHide={() => setShowEditAddressForm(false)}
      />
      <div id="smart-meter-details">
        <Container>
          <Row>
            <Col className="mb-3" lg={8}>
              <Icon colour={primary} icon="smart-meter" className="mb-2" />
              <h2>My Smart Meter</h2>
            </Col>
          </Row>
          {showReconsentBanner && (
            <Row>
              <Col className="mb-3" lg={8}>
                <ReconfirmCard reconsentDue={reconsentDue} />
              </Col>
            </Row>
          )}
          <Row>
            <Col lg={8}>
              <Form.Label>Registered smart meter</Form.Label>
              <StyledCard>
                <Card.Body className="px-2 py-0">
                  <Container>
                    <BorderedRow>
                      <Col>
                        <b>Address</b>:{' '}
                        {currentUser?.attributes?.address
                          ? parseAddressString(currentUser?.attributes?.address)
                          : '-'}
                      </Col>
                    </BorderedRow>
                    <BorderedRow>
                      <Col>
                        <b>MPAN</b>:{' '}
                        <LoadingBarWrapper
                          loading={loading}
                          data={
                            elecMpxn ? (
                              <span>
                                {elecMpxn}{' '}
                                <a href={whatIsMpanLink}>{"What's this?"}</a>
                              </span>
                            ) : (
                              '-'
                            )
                          }
                        />
                      </Col>
                    </BorderedRow>
                    <BorderedRow>
                      <Col>
                        <b>Registered</b>:{' '}
                        <LoadingBarWrapper
                          loading={loading}
                          data={
                            registered ? formatRegisteredDate(registered) : '-'
                          }
                        />
                      </Col>
                    </BorderedRow>
                    <div className="d-none d-md-block">
                      <BorderedRow>
                        <Col md={4}></Col>
                        <Col md={4}>
                          <StyledH4>ELECTRICITY</StyledH4>
                        </Col>
                        <Col md={4}>
                          <StyledH4>GAS</StyledH4>
                        </Col>
                      </BorderedRow>
                      <BorderedRow>
                        <Col md={4}>
                          <StyledRowTitle>Data status:</StyledRowTitle>
                        </Col>
                        <Col md={4}>
                          <LoadingBarWrapper
                            loading={loading}
                            data={
                              !!elecDataRange ? (
                                <div className="d-flex align-items-center">
                                  <img
                                    src={checkIconSvg}
                                    alt="Available icon"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      marginRight: '6px',
                                    }}
                                  />
                                  <span>Data available</span>
                                </div>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <img
                                    src={notAvailableIconSvg}
                                    alt="Not available icon"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      marginRight: '6px',
                                    }}
                                  />
                                  <span>
                                    {/* eslint-disable-next-line max-len */}
                                    Not available.{' '}
                                    <a href={notAvailableLink}>Why?</a>
                                  </span>
                                </div>
                              )
                            }
                          />
                        </Col>
                        <Col md={4}>
                          <LoadingBarWrapper
                            loading={loading}
                            data={
                              !!gasDataRange ? (
                                <div className="d-flex align-items-center">
                                  <img
                                    src={checkIconSvg}
                                    alt="Available icon"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      marginRight: '10px',
                                    }}
                                  />
                                  <span>Data available</span>
                                </div>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <img
                                    src={notAvailableIconSvg}
                                    alt="Not available icon"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      marginRight: '10px',
                                    }}
                                  />
                                  <span>
                                    {/* eslint-disable-next-line max-len */}
                                    Not available.{' '}
                                    <a href={notAvailableLink}>Why?</a>
                                  </span>
                                </div>
                              )
                            }
                          />
                        </Col>
                      </BorderedRow>
                      <BorderedRow>
                        <Col md={4}>
                          <StyledRowTitle>Last updated:</StyledRowTitle>
                        </Col>
                        <Col md={4}>
                          <LoadingBarWrapper
                            loading={loading}
                            data={elecLastUpdated}
                            maxWidth={100}
                          />
                        </Col>
                        <Col md={4}>
                          <LoadingBarWrapper
                            loading={loading}
                            data={gasLastUpdated}
                            maxWidth={100}
                          />
                        </Col>
                      </BorderedRow>
                      <FinalRow>
                        <Col md={4}>
                          <StyledRowTitle>Data range:</StyledRowTitle>
                        </Col>
                        <Col md={4}>
                          <LoadingBarWrapper
                            loading={loading}
                            data={elecDataRange ? `${elecDataRange} days` : '-'}
                            maxWidth={100}
                          />
                        </Col>
                        <Col md={4}>
                          <LoadingBarWrapper
                            loading={loading}
                            data={gasDataRange ? `${gasDataRange} days` : '-'}
                            maxWidth={100}
                          />
                        </Col>
                      </FinalRow>
                    </div>
                    <div className="d-md-none">
                      <BorderedRow>
                        <Col>
                          <StyledH4>ELECTRICITY</StyledH4>
                        </Col>
                      </BorderedRow>
                      <BorderedRow>
                        <Col className="d-flex">
                          <StyledRowTitle>Data status:</StyledRowTitle>
                          <LoadingBarWrapper
                            loading={loading}
                            data={
                              !!elecDataRange ? (
                                <div className="d-flex align-items-center">
                                  <img
                                    src={checkIconSvg}
                                    alt="Available icon"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      marginRight: '6px',
                                    }}
                                  />
                                  <span>Data available</span>
                                </div>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <img
                                    src={notAvailableIconSvg}
                                    alt="Not available icon"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      marginRight: '6px',
                                    }}
                                  />
                                  <span>
                                    {/* eslint-disable-next-line max-len */}
                                    Not available.{' '}
                                    <a href={notAvailableLink}>Why?</a>
                                  </span>
                                </div>
                              )
                            }
                          />
                        </Col>
                      </BorderedRow>
                      <BorderedRow>
                        <Col className="d-flex">
                          <StyledRowTitle>Last updated:</StyledRowTitle>
                          <LoadingBarWrapper
                            loading={loading}
                            data={elecLastUpdated}
                            maxWidth={100}
                          />
                        </Col>
                      </BorderedRow>
                      <BorderedRow>
                        <Col className="d-flex">
                          <StyledRowTitle>Data range:</StyledRowTitle>
                          <LoadingBarWrapper
                            loading={loading}
                            data={elecDataRange ? `${elecDataRange} days` : '-'}
                            maxWidth={100}
                          />
                        </Col>
                      </BorderedRow>
                      <BorderedRow>
                        <Col>
                          <StyledH4>GAS</StyledH4>
                        </Col>
                      </BorderedRow>
                      <BorderedRow>
                        <Col className="d-flex">
                          <StyledRowTitle>Data status:</StyledRowTitle>
                          <LoadingBarWrapper
                            loading={loading}
                            data={
                              !!gasDataRange ? (
                                <div className="d-flex align-items-center">
                                  <img
                                    src={checkIconSvg}
                                    alt="Available icon"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      marginRight: '10px',
                                    }}
                                  />
                                  <span>Data available</span>
                                </div>
                              ) : (
                                <div className="d-flex align-items-center">
                                  <img
                                    src={notAvailableIconSvg}
                                    alt="Not available icon"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      marginRight: '10px',
                                    }}
                                  />
                                  <span>
                                    {/* eslint-disable-next-line max-len */}
                                    Not available.{' '}
                                    <a href={notAvailableLink}>Why?</a>
                                  </span>
                                </div>
                              )
                            }
                          />
                        </Col>
                      </BorderedRow>
                      <BorderedRow>
                        <Col className="d-flex">
                          <StyledRowTitle>Last updated:</StyledRowTitle>
                          <LoadingBarWrapper
                            loading={loading}
                            data={gasLastUpdated}
                            maxWidth={100}
                          />
                        </Col>
                      </BorderedRow>
                      <FinalRow>
                        <Col className="d-flex">
                          <StyledRowTitle>Data range:</StyledRowTitle>
                          <LoadingBarWrapper
                            loading={loading}
                            data={gasDataRange ? `${gasDataRange} days` : '-'}
                            maxWidth={100}
                          />
                        </Col>
                      </FinalRow>
                    </div>
                  </Container>
                </Card.Body>
              </StyledCard>
            </Col>
          </Row>
          <Row>
            <Col lg={8} className="d-flex justify-content-end">
              <Button
                type="button"
                variant="primary"
                className={`px-4 mt-3 mb-1 ${loading ? 'disabled' : ''}`}
                onClick={() => setShowEditAddressForm(true)}
                disabled={loading}>
                Edit my address or smart meter
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

SmartMeterDetails.propTypes = {
  ...currentUserPropTypes,
};

export default SmartMeterDetails;
